// ─────────────────────────────────────────────────────────────
//  DEV HACKS
//  Add new hacks here. Category options:
//  "JavaScript" | "React" | "CSS" | "Git" | "TypeScript" | "Node"
// ─────────────────────────────────────────────────────────────

const devHacks = [
  {
    id: "h1",
    title: "console.table() is criminally underused",
    category: "JavaScript",
    code: `const users = [
  { name: 'Alice', role: 'admin', age: 28 },
  { name: 'Bob',   role: 'user',  age: 32 },
];

console.table(users);
// Renders as a beautiful formatted table in DevTools 🤯`,
    tip: "Way more readable than console.log for arrays of objects. Works in all modern browsers and Node.js.",
  },
  {
    id: "h2",
    title: "structuredClone() - deep copy without hacks",
    category: "JavaScript",
    code: `// ❌ Old hack (loses Dates, undefined, functions, etc.)
  const copy = JSON.parse(JSON.stringify(original));

  // ✅ Modern way
  const copy = structuredClone(original);

  // Works with:
  const obj = {
    date: new Date(),
    map: new Map(),
    nested: { a: 1 }
  };

  const cloned = structuredClone(obj);`,
    tip: "structuredClone handles complex data types correctly and is built into modern browsers and Node.js. No more JSON hacks.",
  },
  {
    id: "h3",
    title: "CSS :has() - the parent selector we always wanted",
    category: "CSS",
    code: `/* Style a card when its input is checked */
.card:has(input:checked) {
  border: 2px solid hotpink;
  background: #fff0f8;
  transform: scale(1.02);
}

/* Style a form that contains an invalid input */
form:has(input:invalid) .submit-btn {
  opacity: 0.5;
  pointer-events: none;
}`,
    tip: "Supported in all modern browsers (Chrome 105+, Firefox 121+, Safari 15.4+). Replaces a huge amount of JavaScript state management.",
  },
  {
    id: "h4",
    title: "Destructure with rename + default value simultaneously",
    category: "JavaScript",
    code: `// Rename AND set a default - in one expression
const { 
  name: userName = 'Guest', 
  age:  userAge  = 0,
  role: userRole = 'viewer'
} = user ?? {};

// Works great with API responses:
const { data: posts = [], error: fetchError = null } = response;`,
    tip: "The pattern is { originalKey: newName = defaultValue }. Extremely useful when consuming APIs where any field might be missing.",
  },
  {
    id: "h5",
    title: "AbortController - cancel fetch requests properly",
    category: "JavaScript",
    code: `// Create a controller
const controller = new AbortController();

fetch('/api/data', { signal: controller.signal })
  .then(res => res.json())
  .then(data => console.log(data))
  .catch(err => {
    if (err.name === 'AbortError') {
      console.log('Request cancelled');
    }
  });

// Cancel the request
controller.abort();`,
    tip: "Essential for search inputs, route changes, and avoiding race conditions. Prevents outdated responses overriding fresh data.",
  },
  {
    id: "h6",
    title: "Git stash with a name - never lose work again",
    category: "Git",
    code: `# Always name your stashes:
git stash push -m "wip: navbar responsive fix"
git stash push -m "wip: dark mode experiment"

# List them clearly:
git stash list
# stash@{0}: On main: wip: dark mode experiment
# stash@{1}: On main: wip: navbar responsive fix

# Apply a specific one:
git stash pop stash@{1}

# Apply WITHOUT removing from stash list:
git stash apply stash@{0}`,
    tip: "Named stashes are a lifesaver when juggling multiple branches or experiments. git stash pop removes it; git stash apply keeps it.",
  },
  {
    id: "h7",
    title: "TypeScript satisfies operator - type-check without widening",
    category: "TypeScript",
    code: `type Color = 'red' | 'green' | 'blue';

// ❌ With 'as' - TypeScript forgets the literal type
const config = { color: 'red' } as { color: Color };
// config.color is now Color, not 'red'

// ✅ With 'satisfies' - validates type, keeps literal
const config = { color: 'red' } satisfies { color: Color };
// config.color is still 'red' - you get autocomplete AND type safety`,
    tip: "The satisfies operator (TypeScript 4.9+) validates that your value matches a type without widening the type. Best of both worlds.",
  },
  {
    id: "h8",
    title: "CSS Grid named areas - layouts that read like English",
    category: "CSS",
    code: `.layout {
  display: grid;
  grid-template-areas:
    "header  header"
    "sidebar main  "
    "footer  footer";
  grid-template-columns: 240px 1fr;
  grid-template-rows: auto 1fr auto;
  min-height: 100vh;
}

.header  { grid-area: header;  }
.sidebar { grid-area: sidebar; }
.main    { grid-area: main;    }
.footer  { grid-area: footer;  }`,
    tip: "The visual grid in your CSS matches the actual layout. Changing the layout is as simple as rearranging the string template. Much more readable than column/row numbers.",
  },
  {
    id: "h9",
    title: "Promise.allSettled - when you want all results, not just success",
    category: "JavaScript",
    code: `// Promise.all - STOPS at first rejection ❌
const results = await Promise.all([
  fetchUser(),   // fails
  fetchPosts(),  // never returns
  fetchComments()
]);

// Promise.allSettled - ALWAYS resolves with all outcomes ✅
const results = await Promise.allSettled([
  fetchUser(),
  fetchPosts(),
  fetchComments()
]);

results.forEach(result => {
  if (result.status === 'fulfilled') console.log(result.value);
  if (result.status === 'rejected')  console.error(result.reason);
});`,
    tip: "Use allSettled when you want to process all results regardless of failures - perfect for dashboards that show partial data rather than a full error state.",
  },
  {
    id: "h10",
    title: "useId for accessible form labels - no more manual IDs",
    category: "React",
    code: `import { useId } from 'react';

function FormField({ label, type = 'text' }) {
  const id = useId(); // Generates stable, unique ID

  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} />
    </div>
  );
}

// Renders: id="r:0:", id="r:1:", etc.
// SSR-safe, collision-proof, zero configuration`,
    tip: "useId is stable across server and client renders, making it SSR-safe unlike Math.random(). Essential for accessible form components in component libraries.",
  },
  {
    id: "h11",
    title: "CSS clamp() - responsive typography without media queries",
    category: "CSS",
    code: `/* clamp(min, preferred, max) */

h1 {
  /* Scales from 28px at 320px viewport
     to 56px at 1200px viewport */
  font-size: clamp(1.75rem, 4vw + 1rem, 3.5rem);
}

p {
  font-size: clamp(0.9rem, 1.5vw + 0.5rem, 1.1rem);
}

.hero {
  padding: clamp(2rem, 8vw, 6rem) 0;
}`,
    tip: "clamp() works for any size property - font-size, padding, gap, margin, width. Combine with vw units for truly fluid scaling with hard limits.",
  },
  {
    id: "h12",
    title: "git bisect - find the exact commit that broke something",
    category: "Git",
    code: `# Start bisecting
git bisect start

# Mark current state as broken
git bisect bad

# Mark a known-good commit (e.g. last week's tag)
git bisect good v2.3.0

# Git checks out a midpoint commit.
# Test it, then tell Git the result:
git bisect good   # if this commit works
git bisect bad    # if this commit is broken

# Git binary-searches until it finds the exact bad commit.
# When done:
git bisect reset`,
    tip: "For large codebases, bisect can find the bad commit in 7–10 steps even across thousands of commits. You can also automate it with git bisect run <test-script>.",
  },
  {
    id: "h13",
    title: "requestIdleCallback - run work when the browser is free",
    category: "JavaScript",
    code: `requestIdleCallback(() => {
  heavyNonCriticalTask();
});

// With timeout fallback
requestIdleCallback(task, { timeout: 2000 });`,
    tip: "Perfect for analytics, logging, or non-urgent work. Keeps UI smooth by avoiding main thread blocking.",
  },
  {
    id: "h14",
    title: "AbortSignal.timeout() — no more manual timeouts",
    category: "JavaScript",
    code: `// Auto-cancel request after 3 seconds
fetch('/api/data', {
  signal: AbortSignal.timeout(3000)
});`,
    tip: "Built-in timeout for fetch without setTimeout hacks. Cleaner and less error-prone.",
  },
  {
    id: "h15",
    title: "git add -p — stage only what you want (line by line)",
    category: "Git",
    code: `# Instead of adding whole files
git add -p

# Git will show chunks (hunks) like:
Stage this hunk [y,n,q,a,d,e,?]?

# Options:
# y → stage this part
# n → skip
# e → manually edit the patch (!!)
# s → split into smaller chunks

# Then commit cleanly:
git commit -m "fix: only actual bug, not console logs"`,
    tip: "Perfect when you’ve mixed changes in one file. Lets you create clean commits without rewriting code or stashing.",
  },
];

export default devHacks;
